/**
 * 🔴 CALENDAR DATES — ONE DEFINITION, SHARED BY THE SERVER AND EVERY FRONT END.
 *
 * ERP Phase 1a increment 1b. The arithmetic did not change when it moved here from
 * `src/common/date/date.ts`; what changed is who may call it, and what happens when the input is bad.
 *
 * ## ⚠️ THE GATE MOVED FIRST, AND THAT ORDER WAS THE WHOLE INCREMENT
 *
 * `scripts/gate-utc-dates.mjs` refused date arithmetic anywhere but `src/common/date/date.ts` — and
 * it walked `src/` only. Moving this file into `packages/` without teaching the gate first would not
 * have satisfied it; it would have ESCAPED it, silently. So the gate and the business-date sweep now
 * walk `src/`, `packages/*\/src` and `web/src`, and THIS file is the one allowed to fall back to UTC.
 *
 * 🔬 AND WIDENING IT FOUND SIX. The browser app had never been inside either gate, and six places in
 * it dated a record with `new Date().toISOString().slice(0, 10)` — a payment, a till sale, a stock
 * transfer's dispatch and arrival, a pick and a receipt. Dhaka is UTC+6: every one of them defaulted
 * to YESTERDAY for anything entered before 06:00. `localToday` below is what they call now.
 *
 * ## 🔴 NO NEST HERE, SO BAD INPUT IS AN `InvalidDateError`
 *
 * Every helper used to throw `BadRequestException`, which is a Nest class and cannot ride into a
 * browser bundle. The server's wrappers in `src/common/date/date.ts` turn this error into the same 400
 * with the same sentence; a front end shows the sentence as it is.
 */

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** A date the caller supplied that is not one. The message is written for the person who typed it. */
export class InvalidDateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidDateError';
  }
}

/**
 * The zone a company's "today" is resolved in. Bangladesh is UTC+6, so a UTC-derived date is
 * YESTERDAY every morning before 06:00 Dhaka — which silently dated reversals, aging as-of dates
 * and tax-rate lookups into the previous (possibly closed) period.
 *
 * The default only. It used to be read from BUSINESS_TIMEZONE by a free function that reached
 * straight into process.env, bypassing ConfigService entirely; it now lives on companies.timezone,
 * because two companies keeping books in different countries have different todays. Seeds still
 * use the environment variable, which is why it survives in the schema.
 */
export const DEFAULT_BUSINESS_TZ = 'Asia/Dhaka';

/**
 * Whether the runtime recognises a zone.
 *
 * Checked at WRITE time, because todayInBusinessTz falls back to UTC on an unrecognised zone rather
 * than throwing — deliberately, since a report should not 500 over a configuration value. But that
 * makes a typo invisible: 'Asia/Dacca' is not a zone Node knows, so it would silently date every
 * reversal, tax-rate lookup and aging report in UTC, with no error anywhere and a six-hour error
 * that only shows up near midnight.
 */
export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Today's calendar date (YYYY-MM-DD) in the business timezone — never `new Date().toISOString()`,
 * which is UTC. Falls back to UTC only if the configured zone is rejected by the runtime.
 */
export function todayInBusinessTz(tz: string, now: Date = new Date()): string {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
  } catch {
    return now.toISOString().slice(0, 10); // unknown zone → UTC rather than throwing
  }
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Today, in the calendar of the machine asking — the default a person at a screen means by "today".
 *
 * ## ⚠️ FOR A FRONT END, NOT FOR THE SERVER
 *
 * A browser in Dhaka has Dhaka's calendar, so this is the day on the wall where the user is sitting.
 * The SERVER must not call it: its host's zone is an accident of deployment, and a business date there
 * comes from `todayInBusinessTz(company.timezone)`.
 *
 * 🔬 Local fields, NOT `toISOString()`. Four screens carried private copies of exactly this, each with
 * a comment explaining why; six others used the UTC slice and were a day out before 06:00.
 */
export function localToday(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** True only for a real calendar date in YYYY-MM-DD form (rejects 2026-13-01, 2026-02-31, …). */
export function isValidIsoDate(s: string): boolean {
  if (typeof s !== 'string' || !ISO.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/** Assert a valid calendar date, throwing `InvalidDateError` with a sentence a person can act on. */
export function assertIsoDate(s: string, field = 'date'): string {
  if (!isValidIsoDate(s)) {
    throw new InvalidDateError(`${field} must be a valid calendar date (YYYY-MM-DD).`);
  }
  return s;
}

/**
 * `issueDate` plus `days`, as a calendar date. Null when there are no terms.
 *
 * ## Why UTC, and why the host's timezone cannot reach it
 *
 * A due date is a CALENDAR fact about a document, not an instant. `issueDate` was already resolved
 * in the company's own zone before it got here, so re-introducing a timezone at this step would
 * shift the result by a day for exactly the companies east of UTC — which is all of them here.
 * Parsing as `…T00:00:00Z` and stepping with `setUTCDate` makes the host's `TZ` irrelevant; the
 * local-fields form (`d.setDate(d.getDate() + n)`) does not.
 *
 * ## The two defects this replaced
 *
 * `InvoicesService.defaultDueDate` was correct about UTC and wrong about two other things, and both
 * failed the same way — SILENTLY, by producing an invoice with no due date at all:
 *
 *   1. `if (Number.isNaN(d.getTime())) return null`. An unparseable issue date yielded a document
 *      that simply never ages: a null due date is forced to 0 days overdue and filed under
 *      `current` at every as-of date, for ever.
 *   2. `@Matches(ISO_DATE)` accepts `2026-02-31`, which `new Date` ROLLS FORWARD into March rather
 *      than rejecting — so the shape check passed, the date silently moved, and the due date was
 *      computed from a day that does not exist.
 *
 * Both are now refusals. `assertIsoDate` rejects the impossible date, and a non-integer term throws
 * rather than producing `NaN` and, through it, `Invalid Date`.
 */
export function addDays(issueDate: string, days: number | null | undefined): string | null {
  if (days === null || days === undefined || days === 0) return null;
  assertIsoDate(issueDate, 'issueDate');
  if (!Number.isInteger(days)) {
    throw new InvalidDateError(`Payment terms must be a whole number of days, not ${days}.`);
  }
  const d = new Date(`${issueDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * WHOLE MONTHS ON FROM A PLAIN DATE, CLAMPED TO THE END OF THE MONTH — ERP Phase 11 increment 9.
 *
 * ## 🔴 THE 31st IS WHY THIS IS A FUNCTION AND NOT `setUTCMonth`
 *
 * `new Date('2026-01-31').setUTCMonth(+1)` gives **3 March**, because JavaScript rolls the overflow
 * forward rather than clamping. ⚠️ A twelve-month schedule starting on 31 January would then fall
 * due on 3 March, 31 March, 1 May — dates nobody chose, drifting further apart as the year goes on,
 * and each one deciding which PERIOD an instalment lands in.
 *
 * 🔬 SO IT CLAMPS: the 31st of January plus one month is the 28th (or 29th) of February, which is
 * what every schedule of instalments in the world means by *"monthly"*. The day-of-month is kept
 * wherever the target month is long enough to hold it, so 31 Jan → 28 Feb → 31 Mar rather than
 * collapsing to the 28th for ever.
 *
 * ⚠️ IT LIVES HERE FOR `daysBetween`'s REASON, stated below: the UTC gate refuses date arithmetic
 * anywhere else, and a private copy of this is where somebody eventually writes the version that
 * rolls into March.
 */
export function addMonths(from: string, months: number): string {
  assertIsoDate(from, 'from');
  if (!Number.isInteger(months)) {
    throw new InvalidDateError(`Months must be a whole number, not ${months}.`);
  }
  const d = new Date(`${from}T00:00:00Z`);
  const day = d.getUTCDate();
  // To the FIRST of the target month, so the overflow JavaScript would roll forward cannot happen —
  // then put the day back, bounded by how long that month actually is.
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const lastOfTarget = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastOfTarget));
  return d.toISOString().slice(0, 10);
}

/**
 * WHOLE DAYS FROM ONE PLAIN DATE TO ANOTHER — ERP Phase 11 increment 4.
 *
 * ## 🔴 IT LIVES HERE BECAUSE THE UTC GATE SAYS SO, AND THE GATE IS RIGHT
 *
 * `gate-utc-dates.spec.ts` refuses `toISOString().slice(0, 10)` and its relatives anywhere but this
 * file. ⚠️ The first draft of the s.60 clock did its own arithmetic and was caught — which is the
 * gate working exactly as intended, because the twentieth copy of *"what day is it"* is where
 * somebody eventually writes one that drifts with the server's timezone.
 *
 * 🔬 BOTH ENDS ARE PLAIN DATES, so UTC midnight is not a timezone decision — it is the same fixed
 * point applied to both, which is what makes the subtraction mean *days* rather than *hours apart*.
 * `todayInBusinessTz` is what decides which DAY the caller is standing in; this only counts between
 * two days somebody has already chosen.
 */
export function daysBetween(from: string, to: string): number {
  assertIsoDate(from, 'from');
  assertIsoDate(to, 'to');
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}
